import { Component, OnInit, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Inject } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import dayjs from "dayjs";

// Services
import { MsalService } from "@azure/msal-angular";
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { UpdateParameterService } from "../../../data/record/update-parameter.service";
import { use } from "echarts";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { USER_DATA_TEMPLATE_DETAILS_I18N } from "./user-data-template-details.i18n";

@Component({
  selector: "app-user-data-template-details",
  templateUrl: "./user-data-template-details.component.html",
  styleUrls: ["./user-data-template-details.component.css"],
  imports: SHARED_IMPORTS,
})
export class UserDataTemplateDetailsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = USER_DATA_TEMPLATE_DETAILS_I18N;

  public mode = Mode.disp;
  Mode = Mode;

  preview;
  errors;

  userDataTemplateFormGroup: FormGroup;

  private readonly defaultUserDataTemplateData: UserDataTemplateData = {
    id: null,

    name: "",

    template: "[]",

    preview: "",
  };

  userDatatemplateData: UserDataTemplateData;
  loginUserId: string;

  constructor(
    public dialogRef: MatDialogRef<UserDataTemplateDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public _matdialog: MatDialog,
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private msalService: MsalService,
    private route: ActivatedRoute,
    private router: Router,
    public snackBar: MatSnackBar,
    public common: DatabaseCacheService,
    private updateParameterService: UpdateParameterService,
  ) {}

  ngOnInit() {
    this.userDatatemplateData =
      this.data?.userDataTemplateData ?? this.defaultUserDataTemplateData;

    // this.resetFields();

    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        let user = this.msalService.instance.getActiveAccount();
        this.loginUserId = user.idTokenClaims["sub"];
      });

    // 入力フォームのフィールド定義
    this.userDataTemplateFormGroup = new FormGroup({
      name: new FormControl("", [Validators.required]),
      template: new FormControl(""),
    });

    this.userDataTemplateFormGroup.disable();

    // 表示データ割り当て
    this.userDataTemplateFormGroup
      .get("name")
      .setValue(this.userDatatemplateData.name);
    this.userDataTemplateFormGroup
      .get("template")
      .setValue(JSON.stringify(this.userDatatemplateData.template));

    this.refreshPreviewAndErrors();

    if (this.userDatatemplateData.id == null) {
      this.switchToEditMode();
    }

    //
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        // this.userId = this.data?.id;
        // this.sectionId = this.common.getSelectedSection().sectionId;
        // this.getStaff();
      });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  refreshPreviewAndErrors() {
    const templateString = this.userDataTemplateFormGroup.get("template").value;
    let errors = formatErrors(templateString);
    if (errors.length == 0) {
      this.errors = [this.i18n.validation.noErrors];
    } else {
      this.errors = errors;
    }

    try {
      const template = JSON.parse(templateString);
      this.preview = getPreview(template);
    } catch (e) {
      this.preview = [this.i18n.validation.jsonParseError];
    }
  }

  switchToEditMode() {
    this.mode = Mode.input;

    this.userDataTemplateFormGroup.get("name").enable();
    this.userDataTemplateFormGroup.get("template").enable();

    return;
  }

  onEditOrUploadClicked() {
    // 現在がディスプレイモードなら編集モードに切り替える
    if (this.mode == Mode.disp) {
      this.switchToEditMode();
      return;
    }

    //そうでない場合はすでに編集モードなので呼び出し元に修正後の値を返す

    //// まずフォーマット確認
    let templateString = this.userDataTemplateFormGroup.value.template;
    let errors = formatErrors(templateString);
    if (errors.length > 0) {
      let snack = this.snackBar.open(
        this.i18n.validation.formatError,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );
      snack
        .afterDismissed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {});

      return;
    }

    var postParams: Record<string, any> = {};

    postParams.id = this.userDatatemplateData.id;
    postParams.name = this.userDataTemplateFormGroup.value.name;
    postParams.template = JSON.parse(templateString);
    postParams.preview = getPreview(postParams.template).join(", ");

    this.dialogRef.close({ updatedData: postParams });
  }
}

export function formatErrors(userDataTemplateString): string[] {
  let errors = [];

  try {
    JSON.parse(userDataTemplateString);
  } catch (e) {
    errors.push(
      USER_DATA_TEMPLATE_DETAILS_I18N.validation.notJsonFormat,
    );

    return errors;
  }

  const template = JSON.parse(userDataTemplateString);
  try {
    for (var i = 0; i < template.length; i++) {
      let section = template[i];

      if (!section.hasOwnProperty("name")) {
        errors.push(
          USER_DATA_TEMPLATE_DETAILS_I18N.validation.sectionMissingName(i),
        );
        continue;
      }

      const requiredKeysSection = [
        "visualizationType",
        "visualizationSubType",
        "description",
        "displayNames",
      ];
      requiredKeysSection.forEach((k) => {
        if (!section.hasOwnProperty(k)) {
          errors.push(
            USER_DATA_TEMPLATE_DETAILS_I18N.validation.sectionMissingAttribute(section.name, k),
          );
        }
      });

      if (section.hasOwnProperty("displayNames") != null) {
        let displayNamesSection = section.displayNames;
        if (
          !(
            displayNamesSection.hasOwnProperty("ja") ||
            displayNamesSection.hasOwnProperty("jp")
          )
        ) {
          errors.push(
            USER_DATA_TEMPLATE_DETAILS_I18N.validation.sectionMissingDisplayNames(section.name),
          );
        }
      }

      if (!section.hasOwnProperty("dataItems")) {
        errors.push(
          USER_DATA_TEMPLATE_DETAILS_I18N.validation.sectionMissingDataItems(section.name),
        );
        continue;
      }

      let dataItems = section.dataItems;
      for (var j = 0; j < dataItems.length; j++) {
        let dataItem = dataItems[j];

        if (!dataItem.hasOwnProperty("name")) {
          errors.push(
            USER_DATA_TEMPLATE_DETAILS_I18N.validation.dataItemMissingName(section.name, j),
          );
          continue;
        }

        const requiredKeysDataItem = [
          "type",
          "enabled",
          "required",
          "showOnSummary",
          "displayNames",
        ];
        requiredKeysDataItem.forEach((k) => {
          if (!dataItem.hasOwnProperty(k)) {
            errors.push(
              USER_DATA_TEMPLATE_DETAILS_I18N.validation.dataItemMissingAttribute(section.name, dataItem.name, k),
            );
          }
        });

        if (dataItem.hasOwnProperty("displayNames") != null) {
          let displayNamesDataItem = dataItem.displayNames;
          if (
            !(
              displayNamesDataItem.hasOwnProperty("ja") ||
              displayNamesDataItem.hasOwnProperty("jp")
            )
          ) {
            errors.push(
              USER_DATA_TEMPLATE_DETAILS_I18N.validation.dataItemMissingDisplayNames(section.name, dataItem.name),
            );
          }
        }
      }
    }
  } catch {
    return errors;
  }

  return errors;
}

export function getPreview(userDataTemplate): string[] {
  let preview: string[] = [];

  try {
    for (var i = 0; i < userDataTemplate.length; i++) {
      let section = userDataTemplate[i];
      let sectionString =
        (section.displayNames.ja ?? section.displayNames.jp) +
        " : (" +
        section.dataItems
          .map((d) => d.displayNames.ja ?? d.displayNames.jp)
          .join(",") +
        ")";
      preview.push(sectionString);
    }
  } catch {
    preview.push(USER_DATA_TEMPLATE_DETAILS_I18N.validation.invalidFormat);
  }

  return preview;
}

export interface KeyValue {
  key: string;
  value: string;
}

export interface InUpdateMember {
  userType: string;
  role: string[];
  privileges: KeyValue[];
  contractStatus: string;
}

export enum Mode {
  disp,
  input,
}

export interface UserDataTemplateData {
  id: number;

  name: string;

  template: string;

  preview: string;
}
